import { Component } from 'react';
import { inject, observer } from 'mobx-react';
import moment from 'moment/min/moment-with-locales';

//components
import withLocalization from '../../../../hoc/withLocalization.js';
import withRemoveDialog from '../../../../hoc/withRemoveDialog';

//elements
import Button from '../../../../components/CustomButton/CustomButton.jsx';
import InputTime from '../../../../components/GenericForm/Widgets/InputTime.js';
import Select from '../../../../elements/Select.js';
import RequiredStar from '../../../../elements/RequiredStar.js';
import TimePickerInput from '../../../../components/TimePicker';

//utils
import { getMinutesFromTimeInput, minutesToTimeInput } from '../../../../utils/timeCalc';
import { timeFormat, totalTimeFormat } from '../../../../utils/dateFormat.js';
import { checkIfTwoTimePeriodIntersects } from '../../../../utils/commonMethods';

//assets
import Edit from '~/assets/img/editing.svg';
import Delete from '~/assets/img/deleting.svg';

//styles
// import './Schedule.css';

class TimesheetOvertimeConfigurations extends Component {
    constructor(props) {
        super(props);
        this.overtimeTypeOptions = Object.values(props.overtimeTypes).map((overtimeType) => ({ value: overtimeType.code, label: overtimeType.name }));
        this.state = {
            timesheetOvertimeConfigs: props.currentTimesheetConfig.timesheetConfigs,
            currentEditingIndex: -1,
            currentEditing: {
                overtime_code: Object.values(props.overtimeTypes)?.[0]?.code || null,
                min_hours_in_mins: props.currentTimesheetConfig.timesheetConfigs.length > 0 ? props.currentTimesheetConfig.timesheetConfigs[0].min_hours_in_mins : (props.defaultMinHours || 150),
                from: 0,
                to: 0
            }
        };
        this.removeTimsheetOvertimeConfig = this.removeTimsheetOvertimeConfig.bind(this);
    }

    handleConfigUpdate = () => {
        const { commonStore, t } = this.props;
        const { currentEditingIndex, timesheetOvertimeConfigs, currentEditing } = this.state;
        if (!this.getAllowMinHours()) currentEditing.min_hours_in_mins = 0;

        if (currentEditing.from === currentEditing.to) {
            return commonStore.addNotification(t('Start and end time cannot be same'), null, 'error');
        }

        const hasIntersection = timesheetOvertimeConfigs.some((existingConfig, index) => {
            if (index === currentEditingIndex) return false;

            return checkIfTwoTimePeriodIntersects(
                { s: existingConfig.from, e: existingConfig.to },
                { s: currentEditing.from, e: currentEditing.to }
            );
        });

        if (hasIntersection) {
            return commonStore.addNotification(t('Intersects with existing overtime configuration'), null, 'error');
        }

        if (currentEditingIndex >= 0) {
            timesheetOvertimeConfigs[currentEditingIndex] = this.state.currentEditing;
        } else {
            timesheetOvertimeConfigs.push(currentEditing);
        }
        this.setState({ timesheetOvertimeConfigs }, () => {
            this.props.updateTimesheetConfigs(this.props.currentTimesheetConfig.key, timesheetOvertimeConfigs)
            this.resetEditing();
        });
        commonStore.addNotification(t('Saved'), null, 'success');
    }

    handleTableActions = (action, index) => {
        const { timesheetOvertimeConfigs } = this.state;
        if (action === 'edit') {
            this.setState({
                currentEditingIndex: index,
                currentEditing: { ...timesheetOvertimeConfigs[index] }
            });
        }
        if (action === 'delete') {
            return this.props.handleDelete(index, (index) => {
                return this.removeTimsheetOvertimeConfig(index);
            });
        }
        return;
    }

    removeTimsheetOvertimeConfig = async (idx) => {
        const { commonStore, t } = this.props;
        const { timesheetOvertimeConfigs } = this.state;
        timesheetOvertimeConfigs.splice(idx, 1);
        this.setState({ timesheetOvertimeConfigs });
        commonStore.addNotification(t('Removed'), null, 'error');
    }

    resetEditing = () => {
        const { overtimeTypes, defaultMinHours } = this.props
        this.setState({
            currentEditingIndex: -1,
            currentEditing: {
                overtime_code: Object.values(overtimeTypes)?.[0]?.code || null,
                min_hours_in_mins: defaultMinHours || 150,
                from: 0,
                to: 0
            }
        });
    }

    getAllowMinHours = () => {
        const { commonStore, isFieldOperative, allowMinHours } = this.props;
        if (allowMinHours !== undefined) return !!allowMinHours;
        const basicRules = commonStore.config && commonStore.config.client && commonStore.config.client.data && commonStore.config.client.data.basicRules;
        if (!basicRules) return false;
        const urgentWorkSettings = basicRules.urgentWorkSettings;
        if (urgentWorkSettings) {
            if (isFieldOperative === true) return !!(urgentWorkSettings.allowMinHoursForFieldWork);
            if (isFieldOperative === false) return !!(urgentWorkSettings.allowMinHoursForCustomerCare);
            return !!(urgentWorkSettings.allowMinHoursForFieldWork || urgentWorkSettings.allowMinHoursForCustomerCare);
        }
        return !!(basicRules.allowMinHoursForUrgentCall);
    }

    checkIfActionIsDisabled = (action) => {
        const { currentEditingIndex, timesheetOvertimeConfigs, currentEditing } = this.state
        if (action === 'delete')
            return currentEditingIndex >= 0 || timesheetOvertimeConfigs.length <= 1;
        if (action === 'edit')
            return currentEditingIndex >= 0;
        if (action === 'save')
            return !currentEditing || !currentEditing?.overtime_code || (this.getAllowMinHours() && !currentEditing?.min_hours_in_mins);
        return false;
    }

    handleInputChange = (name, value) => {
        const { currentEditing } = this.state;
        currentEditing[name] = value;
        this.setState({ currentEditing });
    }


    renderOvertimeList = () => {
        const { t, overtimeTypes, commonStore } = this.props;
        const { timesheetOvertimeConfigs } = this.state
        const dateTimeRules = commonStore.getDateTimeRules();
        const allowMinHours = this.getAllowMinHours();

        if (!Object.keys(overtimeTypes)?.length) {
            return (
                <div className="alert alert-warning mt-2" style={{ width: '100%' }}>
                    {t('Please complete control panel settings for overtime types.')}
                </div>
            );
        }

        if (!timesheetOvertimeConfigs?.length)
            return null;

        return (
            <div className="mt-2" style={{ width: '100%' }}>
                <table className="table table-sm">
                    <thead>
                        <tr>
                            <th>{t('Overtime')}</th>
                            <th>{t('Time Range')}</th>
                            {allowMinHours && <th>{t('Hours')}</th>}
                            <th>{t('Actions')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {timesheetOvertimeConfigs.map((overtime, index) => (
                            <tr key={index}>
                                <td>{overtimeTypes[overtime.overtime_code]?.name || 'Overtime'}</td>
                                <td className="text-muted">
                                    {timeFormat(overtime.from, dateTimeRules)} - {timeFormat(overtime.to, dateTimeRules)}
                                </td>
                                {allowMinHours && (
                                    <td>
                                        <span className="text-muted">
                                            {totalTimeFormat(overtime.min_hours_in_mins, dateTimeRules)}&nbsp;{t('Hours')}
                                        </span>
                                    </td>
                                )}
                                <td style={{ textAlign: 'right' }}>
                                    <div className="d-flex gap-1 justify-content-center">
                                        <Button icon_sm fill
                                            onClick={() => this.handleTableActions('edit', index)}
                                            disabled={this.checkIfActionIsDisabled('edit')}
                                            title={t('Change Configuration')}
                                        >
                                            <img src={Edit} alt={'edit'} />
                                        </Button>
                                        <Button icon_sm_delete fill
                                            onClick={() => this.handleTableActions('delete', index)}
                                            disabled={this.checkIfActionIsDisabled('delete')}
                                            title={t('Delete')}
                                        >
                                            <img src={Delete} alt="delete" />
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    componentWillUnmount = () => {
        this.resetEditing();
    }

    render() {
        const { t, currentTimesheetConfig, commonStore, title } = this.props;
        const { currentEditing, currentEditingIndex } = this.state;
        const dateTimeRules = commonStore.getDateTimeRules();
        const format = dateTimeRules && dateTimeRules.time_format ? dateTimeRules.time_format : 'hh:mm';
        const allowMinHours = this.getAllowMinHours();

        return (
            <>
                {(title || moment(currentTimesheetConfig.key, 'YYYY-MM-DD', true).isValid()) && (
                    <div className='d-flex justify-content-start mb-2'>
                        <h5 className='m-0 font-size-15 my-3' style={{ color: '#2550AC' }}>
                            {title ? title : (
                                <>
                                    {t('Configure hours for timesheet and overtime for')}
                                    &nbsp;<b>{moment(currentTimesheetConfig.key).format('ddd')}, &nbsp;{currentTimesheetConfig.key}</b>
                                </>
                            )}
                        </h5>
                    </div>
                )}
                <div className="row">
                    <div className='col-3'>
                        <div className="form-group">
                            <label>{t('Overtime Type')}<RequiredStar /></label>
                            <Select
                                options={this.overtimeTypeOptions}
                                value={this.overtimeTypeOptions.find((option) => option.value === currentEditing.overtime_code)}
                                onChange={(selected) => this.handleInputChange('overtime_code', selected?.value)}
                                placeholder={t("Select overtime type")}
                            />
                        </div>
                    </div>
                    <div className="col-2">
                        <div className="form-group">
                            <label>{t('From')} <RequiredStar /></label>
                            <TimePickerInput
                                value={minutesToTimeInput(currentEditing.from)}
                                onChange={(value) => this.handleInputChange('from', getMinutesFromTimeInput(value))}
                                format={format === "hh:mm" ? "HH:mm" : format}
                            />
                        </div>
                    </div>
                    <div className="col-2">
                        <div className="form-group">
                            <label>{t('To')} <RequiredStar /></label>
                            <TimePickerInput
                                value={minutesToTimeInput(currentEditing.to)}
                                onChange={(value) => this.handleInputChange('to', getMinutesFromTimeInput(value))}
                                format={format === "hh:mm" ? "HH:mm" : format}
                            />
                        </div>
                    </div>
                    {allowMinHours && (
                        <div className="col-3">
                            <div className="form-group">
                                <label>{t('Minimum hours')} <RequiredStar /></label>
                                <InputTime
                                    value={minutesToTimeInput(currentEditing.min_hours_in_mins)}
                                    onChange={(value) => this.handleInputChange('min_hours_in_mins', getMinutesFromTimeInput(value))}
                                    placeholder="HH:MM"
                                />
                            </div>
                        </div>
                    )}
                    <div className="col-2 d-flex justify-content-end align-items-end">
                        {currentEditingIndex >= 0 ?
                            <>
                                <Button fill me2 icon_sm title={t('Save')}
                                    style={{ width: '40px', height: '40px' }}
                                    disabled={this.checkIfActionIsDisabled('save')}
                                    onClick={this.handleConfigUpdate}>
                                    <i className="fa fa-save fa-xl" />
                                </Button>
                                <Button fill icon_sm title={t('Cancel')}
                                    style={{ width: '40px', height: '40px' }}
                                    onClick={this.resetEditing}>
                                    <i className="fa fa-times fa-xl" />
                                </Button>
                            </> :
                            <Button fill
                                onClick={this.handleConfigUpdate}
                                disabled={this.checkIfActionIsDisabled('save')}
                            >
                                {t('Add')}
                            </Button>
                        }
                    </div>
                </div>
                {this.renderOvertimeList()}
            </>
        );
    }
}

export default inject('commonStore')(withLocalization(withRemoveDialog(observer(TimesheetOvertimeConfigurations))));